/**
 * Public demo-gallery page — no auth required.
 *
 * Reached via /demo/:token from the prospecting outreach email. Shows the
 * 3 before/after pairs full-size + a "Vraag je gratis demo aan" CTA so the
 * makelaar (and anyone they share the link with) can claim a free retouch
 * for their own listing. Designed to be share-friendly between agencies —
 * each shared link converts cold awareness into account signups.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowRight, Sparkles, Loader2, AlertCircle } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Inline i18n table — keeps the public page self-contained without
// polluting the main translation JSON with marketing copy that only
// lives here. NL is the source of truth, FR/EN are direct translations.
const COPY = {
    nl: {
        kicker: "AI demo · vrijblijvend",
        hero_a: "Wat AI doet met de foto's van",
        hero_b: "uw pand",
        sub_lead: "Hieronder ziet u 3 voor/na vergelijkingen op basis van de Immoweb-foto's van",
        sub_city: "in",
        sub_tail: "Elke bewerking is in < 1 minuut gemaakt door onze AI — geen Photoshop, geen wachten.",
        share_note: "Deze demo is gepersonaliseerd voor uw kantoor. Deel gerust met collega's — iedereen krijgt 3 gratis bewerkingen bij registratie.",
        empty: "Geen voor/na voorbeelden beschikbaar.",
        compare_hint: "Sleep om te vergelijken →",
        reason_label: "Waarom deze bewerking ·",
        cta_title: "Wil u dit voor uw eigen panden?",
        cta_lead: "Maak een gratis account aan, upload foto's en zie binnen 60 seconden hetzelfde resultaat. Geen abonnement, geen verplichtingen — 10 gratis credits op huis.",
        cta_primary: "Claim je 10 gratis credits",
        cta_secondary: "Of mail ons rechtstreeks",
        cta_header: "Maak gratis account",
        error_title: "Deze demo bestaat niet (meer)",
        error_home: "Naar de site",
        not_found_default: "Demo niet gevonden",
        trust: "4K · 24/7 · Vastgoed Studio Pro",
        slider_before: "Voor",
        slider_after: "Na (AI)",
        tools_kicker: "Inclusief AI Tools",
        tools_title_a: "Maak elke foto",
        tools_title_b: "verkoop-klaar",
        tools_title_c: "in één klik.",
        tools_lead: "Jouw vastgoed-foto retoucher, 24/7 bereikbaar. 4K kwaliteit, met voor & na slider.",
        tools_chips: ["✨ Opruimen", "🗑️ Vuilbakken weg", "☀️ Blauwe lucht", "🪴 Planten erbij"],
        tools_cta: "Registreer & ontvang 10 credits",
        tools_no_card: "Geen creditcard nodig.",
        pricing_kicker: "Credits kopen",
        pricing_title: "Bijtanken.",
        pricing_subtitle: "Direct betaald · credits meteen beschikbaar",
        pricing_buy: "Koop",
        pricing_most: "Meest gekozen",
        pricing_per_credit: "per credit",
        pricing_credits: "credits",
        pricing_excl_btw: "Excl. BTW",
        pricing_perks: ["Direct beschikbaar na betaling", "Geen vervaldatum", "Officiële BTW-factuur per mail"],
        faq_kicker: "Veelgestelde vragen",
        faq_title: "Nog twijfels?",
        faq_items: [
            { q: "Krijg ik echt 10 gratis credits zonder kaart?", a: "Ja. Maak een gratis account met je e-mailadres. We zetten meteen 10 credits klaar — geen kredietkaart, geen verborgen abonnement, geen verplichting." },
            { q: "Hoe lang duurt een AI-bewerking?", a: "Tussen 20 en 40 seconden per foto, in 4K kwaliteit. Je kan ondertussen al de volgende foto starten." },
            { q: "Welke fotoformaten ondersteunen jullie?", a: "JPG, PNG en HEIC tot 25 MB per foto. Resultaten krijg je terug in dezelfde hoge resolutie." },
            { q: "Kan ik op elk moment stoppen?", a: "Ja. Er is geen abonnement — je koopt credits wanneer je ze nodig hebt. Niet-gebruikte credits vervallen niet." },
        ],
    },
    fr: {
        kicker: "Démo IA · sans engagement",
        hero_a: "Ce que l'IA fait avec les photos de",
        hero_b: "votre bien",
        sub_lead: "Ci-dessous, 3 comparaisons avant/après basées sur les photos Immoweb de",
        sub_city: "à",
        sub_tail: "Chaque retouche est réalisée par notre IA en moins d'1 minute — pas de Photoshop, pas d'attente.",
        share_note: "Cette démo est personnalisée pour votre agence. Partagez-la — chacun reçoit 3 retouches gratuites à l'inscription.",
        empty: "Aucun exemple avant/après disponible.",
        compare_hint: "Glissez pour comparer →",
        reason_label: "Pourquoi cette retouche ·",
        cta_title: "Vous voulez ça pour vos propres biens ?",
        cta_lead: "Créez un compte gratuit, téléchargez vos photos et obtenez le même résultat en 60 secondes. Sans abonnement, sans engagement — 10 crédits offerts.",
        cta_primary: "Réclamez vos 10 crédits gratuits",
        cta_secondary: "Ou envoyez-nous un mail",
        cta_header: "Compte gratuit",
        error_title: "Cette démo n'existe pas (ou plus)",
        error_home: "Vers le site",
        not_found_default: "Démo introuvable",
        trust: "4K · 24/7 · Vastgoed Studio Pro",
        slider_before: "Avant",
        slider_after: "Après (IA)",
        tools_kicker: "Outils IA inclus",
        tools_title_a: "Rendez chaque photo",
        tools_title_b: "prête à vendre",
        tools_title_c: "en un clic.",
        tools_lead: "Votre retoucheur de photos immobilières, disponible 24/7. Qualité 4K, avec slider avant & après.",
        tools_chips: ["✨ Désencombrer", "🗑️ Poubelles", "☀️ Ciel bleu", "🪴 Plantes"],
        tools_cta: "Inscrivez-vous & recevez 10 crédits",
        tools_no_card: "Sans carte bancaire.",
        pricing_kicker: "Acheter des crédits",
        pricing_title: "Rechargez.",
        pricing_subtitle: "Paiement direct · crédits disponibles immédiatement",
        pricing_buy: "Acheter",
        pricing_most: "Plus choisi",
        pricing_per_credit: "par crédit",
        pricing_credits: "crédits",
        pricing_excl_btw: "HTVA",
        pricing_perks: ["Disponible immédiatement", "Pas de date d'expiration", "Facture TVA officielle par e-mail"],
        faq_kicker: "Questions fréquentes",
        faq_title: "Encore des doutes ?",
        faq_items: [
            { q: "Je reçois vraiment 10 crédits gratuits sans carte ?", a: "Oui. Créez un compte gratuit avec votre e-mail. Nous activons immédiatement 10 crédits — sans carte bancaire, sans abonnement caché, sans engagement." },
            { q: "Combien de temps dure une retouche IA ?", a: "Entre 20 et 40 secondes par photo, en qualité 4K. Vous pouvez déjà lancer la suivante en parallèle." },
            { q: "Quels formats de photo acceptez-vous ?", a: "JPG, PNG et HEIC jusqu'à 25 Mo par photo. Vous récupérez les résultats dans la même haute résolution." },
            { q: "Puis-je arrêter à tout moment ?", a: "Oui. Il n'y a pas d'abonnement — vous achetez des crédits quand vous en avez besoin. Les crédits non utilisés n'expirent pas." },
        ],
    },
    en: {
        kicker: "AI demo · no commitment",
        hero_a: "What AI does with the photos of",
        hero_b: "your property",
        sub_lead: "Below are 3 before/after comparisons based on the Immoweb photos of",
        sub_city: "in",
        sub_tail: "Each edit is done by our AI in under 1 minute — no Photoshop, no waiting.",
        share_note: "This demo is personalised for your agency. Feel free to share it — anyone who signs up gets 3 free edits.",
        empty: "No before/after examples available.",
        compare_hint: "Drag to compare →",
        reason_label: "Why this edit ·",
        cta_title: "Want this for your own listings?",
        cta_lead: "Create a free account, upload photos and see the same result in 60 seconds. No subscription, no strings — 10 free credits on the house.",
        cta_primary: "Claim your 10 free credits",
        cta_secondary: "Or mail us directly",
        cta_header: "Free account",
        error_title: "This demo doesn't exist (anymore)",
        error_home: "Back to the site",
        not_found_default: "Demo not found",
        trust: "4K · 24/7 · Vastgoed Studio Pro",
        slider_before: "Before",
        slider_after: "After (AI)",
        tools_kicker: "AI Tools included",
        tools_title_a: "Make every photo",
        tools_title_b: "sale-ready",
        tools_title_c: "in one click.",
        tools_lead: "Your real-estate photo retoucher, available 24/7. 4K quality, with before & after slider.",
        tools_chips: ["✨ Declutter", "🗑️ Bins gone", "☀️ Blue sky", "🪴 Add plants"],
        tools_cta: "Sign up & get 10 credits",
        tools_no_card: "No credit card needed.",
        pricing_kicker: "Buy credits",
        pricing_title: "Top up.",
        pricing_subtitle: "Paid directly · credits available instantly",
        pricing_buy: "Buy",
        pricing_most: "Most chosen",
        pricing_per_credit: "per credit",
        pricing_credits: "credits",
        pricing_excl_btw: "Excl. VAT",
        pricing_perks: ["Available instantly after payment", "No expiry date", "Official VAT invoice by email"],
        faq_kicker: "Frequently asked",
        faq_title: "Still hesitating?",
        faq_items: [
            { q: "Do I really get 10 free credits without a card?", a: "Yes. Create a free account with your email. We activate 10 credits instantly — no credit card, no hidden subscription, no commitment." },
            { q: "How long does an AI edit take?", a: "Between 20 and 40 seconds per photo, in 4K quality. You can already start the next one in parallel." },
            { q: "Which photo formats do you support?", a: "JPG, PNG and HEIC up to 25 MB per photo. You get the results back in the same high resolution." },
            { q: "Can I stop at any time?", a: "Yes. There's no subscription — you buy credits when you need them. Unused credits never expire." },
        ],
    },
    es: {
        kicker: "Demo IA · sin compromiso",
        hero_a: "Lo que la IA hace con las fotos de",
        hero_b: "su propiedad",
        sub_lead: "A continuación, 3 comparaciones antes/después basadas en las fotos del anuncio de",
        sub_city: "en",
        sub_tail: "Cada retoque lo realiza nuestra IA en menos de 1 minuto — sin Photoshop, sin esperas.",
        share_note: "Esta demo está personalizada para su agencia. Compártala con sus compañeros — cada uno recibe 3 retoques gratis al registrarse.",
        empty: "No hay ejemplos antes/después disponibles.",
        compare_hint: "Arrastre para comparar →",
        reason_label: "Por qué este retoque ·",
        cta_title: "¿Quiere esto para sus propias propiedades?",
        cta_lead: "Cree una cuenta gratuita, suba fotos y vea el mismo resultado en 60 segundos. Sin suscripción, sin compromiso — 10 créditos gratis de regalo.",
        cta_primary: "Reclame sus 10 créditos gratis",
        cta_secondary: "O escríbanos directamente",
        cta_header: "Cuenta gratuita",
        error_title: "Esta demo no existe (o ya no)",
        error_home: "Ir al sitio",
        not_found_default: "Demo no encontrada",
        trust: "4K · 24/7 · Vastgoed Studio Pro",
        slider_before: "Antes",
        slider_after: "Después (IA)",
        tools_kicker: "Herramientas IA incluidas",
        tools_title_a: "Haga que cada foto esté",
        tools_title_b: "lista para vender",
        tools_title_c: "en un clic.",
        tools_lead: "Su retocador de fotos inmobiliarias, disponible 24/7. Calidad 4K, con comparador antes y después.",
        tools_chips: ["✨ Ordenar", "🗑️ Sin contenedores", "☀️ Cielo azul", "🪴 Añadir plantas"],
        tools_cta: "Regístrese y reciba 10 créditos",
        tools_no_card: "Sin tarjeta de crédito.",
        pricing_kicker: "Comprar créditos",
        pricing_title: "Recargar.",
        pricing_subtitle: "Pago directo · créditos disponibles al instante",
        pricing_buy: "Comprar",
        pricing_most: "El más elegido",
        pricing_per_credit: "por crédito",
        pricing_credits: "créditos",
        pricing_excl_btw: "IVA no incl.",
        pricing_perks: ["Disponible inmediatamente tras el pago", "Sin fecha de caducidad", "Factura oficial con IVA por correo"],
        faq_kicker: "Preguntas frecuentes",
        faq_title: "¿Aún tiene dudas?",
        faq_items: [
            { q: "¿De verdad recibo 10 créditos gratis sin tarjeta?", a: "Sí. Cree una cuenta gratuita con su correo electrónico. Activamos 10 créditos al instante — sin tarjeta de crédito, sin suscripción oculta, sin compromiso." },
            { q: "¿Cuánto tarda un retoque IA?", a: "Entre 20 y 40 segundos por foto, en calidad 4K. Mientras tanto ya puede lanzar la siguiente." },
            { q: "¿Qué formatos de foto aceptan?", a: "JPG, PNG y HEIC hasta 25 MB por foto. Recibe los resultados en la misma alta resolución." },
            { q: "¿Puedo parar en cualquier momento?", a: "Sí. No hay suscripción — compra créditos cuando los necesite. Los créditos no utilizados no caducan." },
        ],
    },
};

const ACTION_BADGES = {
    nl: {
        sunny:      { label: "Zonnig maken",      emoji: "☀️" },
        declutter:  { label: "Opruimen",          emoji: "🧹" },
        add_plants: { label: "Planten erbij",     emoji: "🌿" },
        restyle:    { label: "Restyle interieur", emoji: "🛋️" },
        relight:    { label: "Lichter maken",     emoji: "✨" },
        blur:       { label: "Privacy-blur",      emoji: "👤" },
    },
    fr: {
        sunny:      { label: "Ciel ensoleillé",   emoji: "☀️" },
        declutter:  { label: "Désencombrement",   emoji: "🧹" },
        add_plants: { label: "Verdure ajoutée",   emoji: "🌿" },
        restyle:    { label: "Restyling intérieur", emoji: "🛋️" },
        relight:    { label: "Éclairage amélioré", emoji: "✨" },
        blur:       { label: "Floutage privé",    emoji: "👤" },
    },
    en: {
        sunny:      { label: "Sunny sky swap",    emoji: "☀️" },
        declutter:  { label: "Decluttered",       emoji: "🧹" },
        add_plants: { label: "Greenery added",    emoji: "🌿" },
        restyle:    { label: "Restyled interior", emoji: "🛋️" },
        relight:    { label: "Brighter lighting", emoji: "✨" },
        blur:       { label: "Privacy blur",      emoji: "👤" },
    },
    es: {
        sunny:      { label: "Cielo soleado",     emoji: "☀️" },
        declutter:  { label: "Ordenado",          emoji: "🧹" },
        add_plants: { label: "Verde añadido",     emoji: "🌿" },
        restyle:    { label: "Interior redecorado", emoji: "🛋️" },
        relight:    { label: "Mejor iluminación", emoji: "✨" },
        blur:       { label: "Difuminado privado", emoji: "👤" },
    },
};

export default function PublicDemoGallery() {
    const { token } = useParams();
    const { i18n } = useTranslation();
    const lang = (COPY[i18n.language] ? i18n.language : "nl");
    const t = COPY[lang];
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await axios.get(`${BACKEND}/api/public/prospecting-demo/${token}`);
                if (alive) setData(res.data);
            } catch (e) {
                if (alive) setError(e.response?.data?.detail || COPY.nl.not_found_default);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [token]);

    // When the draft was sent in a specific language, default the page to
    // that language on FIRST LOAD only — after that the visitor can switch
    // freely via the LanguageSwitcher without us reverting it back.
    const [initialLangApplied, setInitialLangApplied] = useState(false);
    useEffect(() => {
        if (initialLangApplied) return;
        if (data?.language && COPY[data.language] && i18n.language !== data.language) {
            i18n.changeLanguage(data.language);
        }
        if (data?.language) setInitialLangApplied(true);
    }, [data, i18n, initialLangApplied]);

    // Personalize browser tab + Open Graph tags per agency, language-aware.
    useEffect(() => {
        if (!data?.agency_name) return undefined;
        const agency = data.agency_name;
        const city = data.city ? ` ${t.sub_city} ${data.city}` : "";
        const titleTpl = {
            nl: `AI demo voor ${agency}${city} · Vastgoed Studio Pro`,
            fr: `Démo IA pour ${agency}${city} · Vastgoed Studio Pro`,
            en: `AI demo for ${agency}${city} · Vastgoed Studio Pro`,
        }[lang];
        const descTpl = {
            nl: `Voor/na vergelijkingen van ${agency} — door AI verwerkt in 60 sec. Bekijk de transformatie en vraag uw eigen gratis demo aan.`,
            fr: `Comparaisons avant/après de ${agency} — traitées par l'IA en 60 sec. Voyez la transformation et demandez votre démo gratuite.`,
            en: `Before/after comparisons of ${agency} — AI-processed in 60 sec. See the transformation and request your own free demo.`,
        }[lang];
        const prevTitle = document.title;
        document.title = titleTpl;
        const updates = [
            { sel: 'meta[property="og:title"]', attr: "content", value: titleTpl },
            { sel: 'meta[property="og:description"]', attr: "content", value: descTpl },
            { sel: 'meta[name="twitter:title"]', attr: "content", value: titleTpl },
            { sel: 'meta[name="twitter:description"]', attr: "content", value: descTpl },
            { sel: 'meta[name="description"]', attr: "content", value: descTpl },
        ];
        const prev = updates.map(({ sel, attr }) => {
            const el = document.querySelector(sel);
            return el ? { el, attr, value: el.getAttribute(attr) } : null;
        });
        updates.forEach(({ sel, attr, value }) => {
            const el = document.querySelector(sel);
            if (el) el.setAttribute(attr, value);
        });
        return () => {
            document.title = prevTitle;
            prev.forEach((p) => { if (p) p.el.setAttribute(p.attr, p.value || ""); });
        };
    }, [data, lang, t]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FAFAFA] grid place-items-center" data-testid="public-demo-loading">
                <Loader2 className="h-8 w-8 animate-spin text-[#E5A93D]" />
            </div>
        );
    }
    if (error) {
        return (
            <div className="min-h-screen bg-[#FAFAFA] grid place-items-center px-6 text-center" data-testid="public-demo-error">
                <div className="max-w-md">
                    <AlertCircle className="h-10 w-10 text-zinc-500 mx-auto mb-4" />
                    <h1 className="font-display text-2xl font-bold text-zinc-950 mb-2">{t.error_title}</h1>
                    <p className="text-sm text-zinc-500 mb-6">{error}</p>
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 h-10 px-5 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm"
                        data-testid="public-demo-error-home"
                    >
                        {t.error_home} <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </div>
        );
    }

    const { agency_name, listing_title, listing_url, city, pairs = [] } = data || {};

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950" data-testid="public-demo-gallery">
            {/* Branded header */}
            <header className="border-b border-white/10 bg-black/40 backdrop-blur">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
                    <Link to="/" className="flex items-center gap-3 group min-w-0" data-testid="public-demo-brand">
                        <img
                            src="/social-factory-logo.png"
                            alt="Social Factory"
                            className="h-9 w-auto opacity-90 group-hover:opacity-100 transition invert"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                        <div className="min-w-0">
                            <div className="font-display text-sm font-bold leading-tight truncate">Vastgoed Studio Pro</div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[#E5A93D]">by social factory</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2 shrink-0">
                        <LanguageSwitcher variant="compact" />
                        <Link
                            to="/register"
                            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-xs transition active:scale-[0.98]"
                            data-testid="public-demo-cta-header"
                        >
                            <Sparkles className="h-3.5 w-3.5" /> {t.cta_header}
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero / explainer */}
            <section className="max-w-5xl mx-auto px-6 pt-10 pb-6">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#E5A93D] font-bold mb-2">{t.kicker}</p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight mb-3" data-testid="public-demo-title">
                    {t.hero_a}{" "}
                    <span className="text-[#E5A93D]">{agency_name || t.hero_b}</span>
                </h1>
                {listing_title && (
                    <p className="text-zinc-600 text-base mb-2 max-w-3xl">
                        {t.sub_lead}{" "}
                        {listing_url ? (
                            <a href={listing_url} target="_blank" rel="noreferrer" className="text-[#E5A93D] hover:underline" data-testid="public-demo-listing-link">
                                {listing_title}
                            </a>
                        ) : (
                            <strong>{listing_title}</strong>
                        )}
                        {city && ` ${t.sub_city} ${city}`}. {t.sub_tail}
                    </p>
                )}
                <p className="text-xs text-zinc-500 max-w-3xl mt-4">
                    {t.share_note}
                </p>
            </section>

            {/* 3 before/after pairs */}
            <section className="max-w-5xl mx-auto px-6 pb-12 space-y-10">
                {pairs.length === 0 && (
                    <div className="text-center text-zinc-500 py-10">{t.empty}</div>
                )}
                {pairs.map((p, i) => (
                    <PairBlock key={i} pair={p} index={i} lang={lang} t={t} />
                ))}
            </section>

            {/* AI Tools spotlight + pricing — convince visitors that THEY can do this themselves. */}
            <ToolsAndPricing t={t} lang={lang} />

            {/* Final CTA strip */}
            <section className="border-t border-zinc-200 bg-gradient-to-b from-[#FAFAFA] to-white">
                <div className="max-w-5xl mx-auto px-6 py-14 text-center">
                    <h2 className="font-display text-2xl sm:text-3xl font-bold leading-tight mb-3" data-testid="public-demo-cta-title">
                        {t.cta_title}
                    </h2>
                    <p className="text-zinc-500 max-w-xl mx-auto mb-6">
                        {t.cta_lead}
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        <Link
                            to="/register"
                            className="inline-flex items-center gap-2 h-12 px-7 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm shadow-[0_8px_24px_-8px_rgba(229,169,61,0.6)] transition active:scale-[0.98]"
                            data-testid="public-demo-cta-register"
                        >
                            <Sparkles className="h-4 w-4" /> {t.cta_primary}
                        </Link>
                        <a
                            href="mailto:info@social-factory.be?subject=AI%20Vastgoed%20demo"
                            className="inline-flex items-center gap-1.5 h-12 px-5 rounded-sm border border-zinc-200 text-zinc-800 hover:bg-zinc-100 text-sm font-semibold"
                            data-testid="public-demo-cta-email"
                        >
                            {t.cta_secondary}
                        </a>
                    </div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-8">
                        {t.trust}
                    </p>
                </div>
            </section>

            <footer className="border-t border-zinc-200 py-6 text-center">
                <p className="text-[11px] text-zinc-400">
                    © {new Date().getFullYear()} Social Factory · <Link to="/" className="text-zinc-500 hover:text-zinc-600">vastgoed.social-factory.be</Link>
                </p>
            </footer>
        </div>
    );
}


function ToolsAndPricing({ t, lang }) {
    // Three tiers — matches CreditPurchase.jsx exactly so visitors see the
    // same pricing they'll get after registering.
    const tiers = [
        { id: "starter", credits: 10,  price: 19,  per: "1.90", highlight: false },
        { id: "pro",     credits: 50,  price: 69,  per: "1.38", highlight: true },
        { id: "studio",  credits: 150, price: 169, per: "1.13", highlight: false },
    ];
    const tierNames = {
        nl: { starter: "Starter", pro: "Pro", studio: "Studio" },
        fr: { starter: "Starter", pro: "Pro", studio: "Studio" },
        en: { starter: "Starter", pro: "Pro", studio: "Studio" },
    }[lang];
    return (
        <>
            {/* AI Tools spotlight */}
            <section className="border-t border-zinc-200 bg-gradient-to-br from-[#EFF6FF] via-white to-white">
                <div className="max-w-5xl mx-auto px-6 py-14">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#E5A93D] font-bold mb-4 inline-flex items-center gap-2">
                        <Sparkles className="h-3 w-3" /> {t.tools_kicker}
                    </p>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight mb-4">
                        {t.tools_title_a}{" "}
                        <em className="text-[#E5A93D] not-italic" style={{ fontStyle: "italic" }}>{t.tools_title_b}</em>{" "}
                        {t.tools_title_c}
                    </h2>
                    <p className="text-zinc-600 max-w-2xl mb-7">{t.tools_lead}</p>
                    <div className="flex flex-wrap gap-2 mb-7" data-testid="public-demo-tool-chips">
                        {t.tools_chips.map((chip, i) => (
                            <span key={i} className="inline-flex items-center h-9 px-3 rounded-sm border border-zinc-200 bg-white text-sm text-zinc-800">
                                {chip}
                            </span>
                        ))}
                    </div>
                    <Link
                        to="/register"
                        className="inline-flex items-center gap-2 h-12 px-7 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm shadow-[0_8px_24px_-8px_rgba(229,169,61,0.6)] transition active:scale-[0.98]"
                        data-testid="public-demo-tools-cta"
                    >
                        <Sparkles className="h-4 w-4" /> {t.tools_cta} <ArrowRight className="h-4 w-4" />
                    </Link>
                    <p className="text-[11px] text-zinc-500 mt-3">{t.tools_no_card}</p>
                </div>
            </section>

            {/* Pricing tiers */}
            <section className="border-t border-zinc-200 bg-zinc-50">
                <div className="max-w-5xl mx-auto px-6 py-14">
                    <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-2">
                                ◐ {t.pricing_kicker}
                            </p>
                            <h2 className="font-display text-3xl sm:text-4xl font-bold leading-tight">{t.pricing_title}</h2>
                        </div>
                        <p className="text-[11px] text-zinc-500">{t.pricing_subtitle}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="public-demo-pricing">
                        {tiers.map((tier) => (
                            <div
                                key={tier.id}
                                className={`relative border rounded-sm bg-white p-5 flex flex-col ${
                                    tier.highlight ? "border-[#E5A93D] ring-1 ring-[#E5A93D]/30" : "border-zinc-200"
                                }`}
                                data-testid={`public-demo-pricing-${tier.id}`}
                            >
                                {tier.highlight && (
                                    <span className="absolute -top-3 left-4 inline-flex items-center gap-1 h-6 px-2 rounded-sm bg-[#E5A93D] text-black text-[10px] uppercase tracking-[0.1em] font-bold">
                                        ★ {t.pricing_most}
                                    </span>
                                )}
                                <div className="flex items-start justify-between gap-3 mb-4">
                                    <h3 className="font-display text-2xl font-bold">{tierNames[tier.id]}</h3>
                                    <div className="text-right">
                                        <div className={`font-display text-3xl font-bold ${tier.highlight ? "text-[#E5A93D]" : "text-zinc-950"}`}>
                                            €{tier.price}
                                        </div>
                                        <div className="text-[9px] uppercase tracking-[0.14em] text-zinc-500 mt-0.5">
                                            {t.pricing_excl_btw}
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <p className="text-base text-zinc-950"><strong>{tier.credits}</strong> {t.pricing_credits}</p>
                                    <p className="text-[11px] text-zinc-500">€{tier.per} {t.pricing_per_credit}</p>
                                </div>
                                <ul className="text-[11px] text-zinc-500 space-y-1.5 mb-5 flex-1">
                                    {t.pricing_perks.map((p, i) => (
                                        <li key={i} className="flex items-center gap-1.5">
                                            <span className="text-emerald-600">✓</span> {p}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to="/register"
                                    className={`inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-sm font-bold text-sm transition active:scale-[0.98] ${
                                        tier.highlight
                                            ? "bg-[#E5A93D] hover:bg-[#C8922C] text-black"
                                            : "bg-white text-zinc-950 hover:bg-zinc-100"
                                    }`}
                                    data-testid={`public-demo-pricing-buy-${tier.id}`}
                                >
                                    <Sparkles className="h-4 w-4" /> {t.pricing_buy} {tierNames[tier.id]}
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ accordion — kills last-mile hesitation before signup. */}
            <section className="border-t border-zinc-200 bg-white">
                <div className="max-w-3xl mx-auto px-6 py-14">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[#E5A93D] font-bold mb-2">
                        ◐ {t.faq_kicker}
                    </p>
                    <h2 className="font-display text-2xl sm:text-3xl font-bold leading-tight mb-8">
                        {t.faq_title}
                    </h2>
                    <div className="space-y-2" data-testid="public-demo-faq">
                        {t.faq_items.map((item, i) => (
                            <details
                                key={i}
                                className="group border border-white/10 rounded-sm bg-black/40 open:border-[#E5A93D]/40 open:bg-[#E5A93D]/[0.04] transition"
                                data-testid={`public-demo-faq-${i}`}
                            >
                                <summary className="list-none cursor-pointer flex items-start justify-between gap-3 px-4 py-3.5 select-none">
                                    <span className="font-display text-sm sm:text-base font-bold text-white pr-4">
                                        {item.q}
                                    </span>
                                    <span className="shrink-0 mt-0.5 h-5 w-5 grid place-items-center text-[#E5A93D] text-lg font-bold transition-transform group-open:rotate-45" aria-hidden>
                                        +
                                    </span>
                                </summary>
                                <div className="px-4 pb-4 -mt-1 text-sm text-zinc-600 leading-relaxed">
                                    {item.a}
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}


function PairBlock({ pair, index, lang, t }) {
    const action = pair.action_preset || "relight";
    const badge = (ACTION_BADGES[lang] || ACTION_BADGES.nl)[action] || { label: "AI", emoji: "✨" };
    const reason = pair.score?.reason;
    return (
        <article
            className="border border-white/10 rounded-sm overflow-hidden bg-black/60"
            data-testid={`public-demo-pair-${index}`}
        >
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-zinc-200">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-lg" aria-hidden>{badge.emoji}</span>
                    <h3 className="font-display text-base sm:text-lg font-bold truncate">{badge.label}</h3>
                </div>
                <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 shrink-0 hidden sm:inline">
                    {t.compare_hint}
                </span>
            </div>
            <div className="bg-zinc-50">
                <BeforeAfterSlider
                    beforeUrl={`${BACKEND}${pair.before_url}`}
                    afterUrl={`${BACKEND}${pair.after_url}`}
                    className="max-h-[700px]"
                    beforeLabel={t.slider_before}
                    afterLabel={t.slider_after}
                />
            </div>
            {reason && (
                <div className="px-4 py-3 border-t border-zinc-200 text-xs text-zinc-500">
                    <span className="text-zinc-500">{t.reason_label} </span>
                    {reason}
                </div>
            )}
        </article>
    );
}
